/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface MedicalFacilityPhysicianProps {
  id: string;
  physicianId: string;
  medicalFacilityId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class MedicalFacilityPhysicianRecord extends Record<MedicalFacilityPhysicianProps>({
  id: null as any,
  physicianId: null as any,
  medicalFacilityId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    physicianId: string;
    medicalFacilityId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): MedicalFacilityPhysicianRecord {
    if (json == null) {
      throw new Error('MedicalFacilityPhysicianRecord.fromJS: parameter cannot be null');
    }
    return new MedicalFacilityPhysicianRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'MedicalFacilityPhysicianJson.id',
      ),
      physicianId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'physicianId', '00000000-0000-0000-0000-000000000000'),
        'MedicalFacilityPhysicianJson.physicianId',
      ),
      medicalFacilityId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'medicalFacilityId', '00000000-0000-0000-0000-000000000000'),
        'MedicalFacilityPhysicianJson.medicalFacilityId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    physicianId: string;
    medicalFacilityId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [MedicalFacilityPhysicianRecord, Error[]] {
    if (json == null) {
      throw new Error('MedicalFacilityPhysicianRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new MedicalFacilityPhysicianRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'MedicalFacilityPhysicianJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      physicianId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'physicianId', '00000000-0000-0000-0000-000000000000'),
            'MedicalFacilityPhysicianJson.physicianId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      medicalFacilityId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) =>
              propValueOrDefault(j, 'medicalFacilityId', '00000000-0000-0000-0000-000000000000'),
            'MedicalFacilityPhysicianJson.medicalFacilityId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: MedicalFacilityPhysicianProps) {
    super(props);
    if (props == null) {
      throw new Error('MedicalFacilityPhysicianRecord constructor parameter cannot be null');
    }
  }
}

export abstract class MedicalFacilityPhysicianViewModelBase
  implements MedicalFacilityPhysicianProps
{
  protected readonly _props: MedicalFacilityPhysicianProps;
  private _json?: MedicalFacilityPhysicianProps;

  get id(): string {
    return this._props.id;
  }
  get physicianId(): string {
    return this._props.physicianId;
  }
  get medicalFacilityId(): string {
    return this._props.medicalFacilityId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  constructor(props: MedicalFacilityPhysicianProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        physicianId: this._props.physicianId,
        medicalFacilityId: this._props.medicalFacilityId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllMedicalFacilityPhysicianEntities = (state: AppState) =>
  state.entityStores.medicalFacilityPhysician.entities;

export const selectMedicalFacilityPhysicianById = (id: string) => (state: AppState) =>
  state.entityStores.medicalFacilityPhysician.entities.get(id);

export const selectMedicalFacilityPhysicianMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.medicalFacilityPhysician.meta.get(id);

export const selectMedicalFacilityPhysicianAndMetadataById = (id: string) =>
  createSelector(
    selectMedicalFacilityPhysicianById(id),
    selectMedicalFacilityPhysicianMetadataById(id),
    (medicalFacilityPhysician, meta) => ({ medicalFacilityPhysician, meta }),
  );

export function selectMedicalFacilityPhysicianEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllMedicalFacilityPhysicianEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, MedicalFacilityPhysicianRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectMedicalFacilityPhysicianEntitiesByPhysicianId(physicianId: string) {
  return createSelector(selectAllMedicalFacilityPhysicianEntities, (entities) =>
    entities.filter((e) => e.physicianId === physicianId).valueSeq(),
  );
}

export function selectMedicalFacilityPhysicianEntitiesByMedicalFacilityId(
  medicalFacilityId: string,
) {
  return createSelector(selectAllMedicalFacilityPhysicianEntities, (entities) =>
    entities.filter((e) => e.medicalFacilityId === medicalFacilityId).valueSeq(),
  );
}
//#endregion Selectors
